// MorningNewBag.jsx — the "New Bag" branch of Morning Mode.
//
// Agent E. Branch-gated: the recipe stays HIDDEN until a bag exists. Two ways in
// (never a dead end): scan the bag's label with the camera (vision → prefilled,
// editable form) or re-open a bag from history. Either way we create/select the
// bean, then generate the first recipe for it — AI when the server's reachable,
// the app's deterministic computeBrew (with a taste-tuned baseline safety net)
// when it isn't. The recipe card matches MorningCoffee exactly; "Started brew"
// runs the same log → schedule-rating-push → complete transaction.
//
// Store seam (props.store): beans, preferences, tasteProfile, userId, addBean,
// uploadEntityPhoto, computeBrew, logBrew, completeMorning. All AI goes through
// the server endpoints in src/lib/morning-ai.js (Clerk-authed via getToken).

import { useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { ChevronDown } from "lucide-react";
import { GlassCard, PrimaryButton, SecondaryButton, TonalButton, Segmented, SpecTiles, Eyebrow, ListRow, Chip } from "./pronto";
import { AidenProfileBanner } from "./AidenProfileBanner";
import { RecipeNotes } from "./RecipeNotes";
import { PhotoCapture } from "./PhotoCapture";
import { visionBagPhoto, generateBrewRecipe, recipeToBrew } from "../lib/morning-ai";
import { scheduleRatingPush } from "../lib/scheduled-push";
import { supabase } from "../lib/supabase";

const DEFAULT_CUPS = 8; // spec default
const CUPS = [{ id: 4, label: "4" }, { id: 8, label: "8" }, { id: 10, label: "10" }, { id: 12, label: "12" }];
const MODES = [{ id: "hot", label: "Hot" }, { id: "cold", label: "Cold brew" }];
const ROASTS = [{ id: "light", label: "Light" }, { id: "medium", label: "Medium" }, { id: "dark", label: "Dark" }];

// Collapse whatever the label / vision says into the 3-way confirm control.
function normalizeRoast(v) {
  const s = String(v || "").toLowerCase();
  if (s.includes("light") || s.includes("blonde")) return "light";
  if (s.includes("dark") || s.includes("french") || s.includes("italian")) return "dark";
  return "medium";
}

const CtoF = (c) => (c == null ? null : Math.round((c * 9) / 5 + 32));
const FtoC = (f) => Math.round(((f - 32) * 5) / 9);

// Last-resort recipe if the AI endpoint is down AND computeBrew can't see the
// just-created bean yet (fresh-state timing). Seeded from the known-good hot
// baseline (65.4 g @ 8 cups, Ode 5.4, 200°F, 1:15, 35 s bloom) — smoother/sweeter
// by design. Cold keeps the Aiden's 176°F bloom-temp floor. Never a dead end.
//
// Grind is 5.4, not the 8.1 this used to carry: docs/coffee-handoff.md settles the
// hot band at Ode 5.3–5.6 and explicitly marks 8.1 stale. 8.1 is cold-brew
// coarseness, so the hot fallback was under-extracting badly.
function baselineBrew(bean, cups, mode) {
  const dose = Math.round(65.4 * (cups / 8) * 10) / 10;
  const ratio = 15;
  const cold = mode === "cold";
  return {
    bean_id: bean.id,
    dose_g: dose,
    water_g: Math.round(dose * ratio),
    grind: { setting: cold ? 8.2 : 5.4 },
    temp_c: FtoC(200),
    profile: { id: cold ? "cold" : "balanced" },
    aidenProfile: { ratio, bloomDuration: 35, bloomTemp: cold ? 176 : 195, temp: 200 },
    source: "baseline",
    rationale: "Baseline dialed to your usual — smooth, sweet, easy on the stomach.",
  };
}

/* ============================ Field primitives ============================ */

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block font-sf text-[11px] font-semibold uppercase tracking-[0.06em] text-pronto-text-3 mb-1">{label}</span>
      {children}
    </label>
  );
}

const INPUT =
  "w-full h-10 px-3 rounded-[11px] bg-[var(--pg-6)] border border-[var(--p-sep-12)] text-pronto-ink font-sf text-[15px] placeholder:text-pronto-text-4 focus:outline-none focus:border-pronto-navy";

function TextInput(props) {
  return <input {...props} className={INPUT} />;
}

/* ============================ Recipe card ============================ */
// Matches MorningCoffee's recipe visual: beans header, big dose + Ode grind,
// Temp / Ratio / Bloom SpecTiles, rationale line.
function RecipeCard({ bean, brew, cups, rationale }) {
  const tempF = CtoF(brew?.temp_c);
  const ratio = brew?.aidenProfile?.ratio ?? brew?.ratio;
  const bloomS = brew?.aidenProfile?.bloomDuration ?? brew?.bloom_s;
  return (
    <>
      {/* Beans */}
      <div className="mb-3">
        <div className="font-round font-bold text-[17px] text-pronto-ink leading-tight">{bean.name}</div>
        <div className="text-[12.5px] text-pronto-text-2">
          {[bean.roaster, bean.roast_level].filter(Boolean).join(" · ")}
        </div>
      </div>

      {/* Dose + grind */}
      <div className="flex items-end gap-5 mb-4">
        <div>
          <div className="font-round font-bold text-[34px] text-pronto-ink leading-none tabular">
            {brew?.dose_g != null ? `${Math.round(brew.dose_g)}g` : "—"}
          </div>
          <div className="text-[11px] uppercase tracking-wide text-pronto-text-3 mt-1">for {cups} cups</div>
        </div>
        <div>
          <div className="font-round font-bold text-[34px] text-pronto-ink leading-none tabular">
            {brew?.grind?.setting ?? "—"}
          </div>
          <div className="text-[11px] uppercase tracking-wide text-pronto-text-3 mt-1">Ode grind</div>
        </div>
      </div>

      {(tempF || ratio || bloomS) && (
        <SpecTiles
          className="mb-4"
          items={[
            { label: "Temp", value: tempF ? `${tempF}°` : "—" },
            { label: "Ratio", value: ratio ? `1:${ratio}` : "—" },
            { label: "Bloom", value: bloomS ? `${bloomS}s` : "—" },
          ]}
        />
      )}

      {rationale && (
        <div className="text-[12.5px] text-pronto-text-2 leading-snug mb-4">{rationale}</div>
      )}
    </>
  );
}

/* ============================ Bean form ============================ */

function BeanForm({ initial, mode, setMode, cups, setCups, busy, onSubmit, onBack }) {
  const [roaster, setRoaster] = useState(initial.roaster || "");
  const [name, setName] = useState(initial.name || "");
  const [roast, setRoast] = useState(normalizeRoast(initial.roast_level));
  const [process, setProcess] = useState(initial.process || "");
  const [roastDate, setRoastDate] = useState(initial.roast_date || "");
  const [origin, setOrigin] = useState(Array.isArray(initial.origin) ? initial.origin.join(", ") : (initial.origin || ""));
  const [weight, setWeight] = useState(initial.label_weight_g != null ? String(initial.label_weight_g) : "");

  const submit = () => {
    if (busy) return;
    onSubmit({
      roaster: roaster.trim() || null,
      name: name.trim() || "New bag",
      roast_level: roast,
      process: process.trim() || null,
      roast_date: roastDate || null,
      origin: origin.split(",").map((s) => s.trim()).filter(Boolean),
      label_weight_g: weight ? Number(weight) : null,
    });
  };

  return (
    <div className="space-y-3">
      {initial._lowConfidence && (
        <div className="text-[12px] text-pronto-text-2 leading-snug rounded-[11px] bg-[var(--pg-6)] px-3 py-2">
          Couldn't read the label cleanly — fill in what you can. Anything's editable.
        </div>
      )}

      <Field label="Name">
        <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Hair Bender" />
      </Field>
      <Field label="Roaster">
        <TextInput value={roaster} onChange={(e) => setRoaster(e.target.value)} placeholder="e.g. Stumptown" />
      </Field>

      <Field label="Roast level">
        <Segmented options={ROASTS} value={roast} onChange={setRoast} />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Process">
          <TextInput
            value={process}
            onChange={(e) => setProcess(e.target.value)}
            placeholder="washed"
            aria-describedby="newbag-process-hint"
          />
        </Field>
        <Field label="Roast date">
          <TextInput type="date" value={roastDate || ""} onChange={(e) => setRoastDate(e.target.value)} />
        </Field>
      </div>
      {/* Inline (not a tooltip — this has to be readable on touch): "process" is
          jargon off the bag's label, and it's a real input to the recipe. */}
      <div id="newbag-process-hint" className="-mt-1 text-[11.5px] text-pronto-text-2 leading-snug">
        Process is how the cherry was handled after picking — washed tastes cleaner and brighter, natural fruitier and
        sweeter, honey lands in between. Pronto factors it into your brew recommendations.
      </div>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Origin">
          <TextInput value={origin} onChange={(e) => setOrigin(e.target.value)} placeholder="Ethiopia" />
        </Field>
        <Field label="Bag weight (g)">
          <TextInput type="number" inputMode="numeric" value={weight} onChange={(e) => setWeight(e.target.value)} placeholder="340" />
        </Field>
      </div>

      <Field label="Brew">
        <Segmented options={MODES} value={mode} onChange={setMode} />
      </Field>
      <Field label="Cups">
        <Segmented options={CUPS} value={cups} onChange={setCups} />
      </Field>

      <div className="flex gap-2 pt-1">
        <SecondaryButton onClick={onBack} disabled={busy} className="flex-1">Back</SecondaryButton>
        <PrimaryButton onClick={submit} disabled={busy} className="flex-1">
          {busy ? "Adding…" : "Add bag & get recipe"}
        </PrimaryButton>
      </div>
    </div>
  );
}

/* ============================ History picker ============================ */

function HistoryPicker({ beans, busy, onPick, onBack }) {
  const rows = useMemo(
    () => (beans || []).slice().sort((a, b) => {
      // active bags first, then most-recently created
      const af = a.status === "finished" ? 1 : 0;
      const bf = b.status === "finished" ? 1 : 0;
      if (af !== bf) return af - bf;
      return String(b.created_at || "").localeCompare(String(a.created_at || ""));
    }),
    [beans],
  );

  return (
    <div className="space-y-3">
      <div className="text-[12.5px] text-pronto-text-2">Pick a bag to brew from again.</div>
      {rows.length === 0 ? (
        <div className="text-[13px] text-pronto-text-3 py-4 text-center">No bags in your history yet.</div>
      ) : (
        <div className="rounded-[14px] overflow-hidden border border-[var(--p-sep-12)] max-h-[46vh] overflow-y-auto">
          {rows.map((b) => (
            <ListRow
              key={b.id}
              label={b.name}
              sublabel={[b.roaster, b.roast_level].filter(Boolean).join(" · ") || undefined}
              onClick={() => !busy && onPick(b)}
              chevron
            >
              {b.status === "finished" && <Chip tone="rest" className="mr-1">finished</Chip>}
            </ListRow>
          ))}
        </div>
      )}
      <SecondaryButton onClick={onBack} disabled={busy} className="w-full">Back</SecondaryButton>
    </div>
  );
}

/* ============================ Main ============================ */

// Bare inside the MorningCoffee one-card frame; own card standalone.
function Card({ embedded, children }) {
  return embedded ? <div>{children}</div> : <GlassCard className="p-5">{children}</GlassCard>;
}

export function MorningNewBag({ store, session, resolved, onComplete, embedded = false }) {
  const { getToken } = useAuth();

  // Latest-store ref so the deterministic fallback can see a just-created bean
  // even if this async closure captured a stale store prop.
  const storeRef = useRef(store);
  storeRef.current = store;

  const [mode, setMode] = useState("hot");
  const [cups, setCups] = useState(DEFAULT_CUPS);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Seed the form from a vision-detected bag if the branch resolver handed one in.
  const detected = resolved?.newBag || null;

  // phase: "choose" (scan / history / manual) → "form" → "recipe"
  const [phase, setPhase] = useState(detected ? "form" : "choose");
  const [formInitial, setFormInitial] = useState(detected || {});
  const [pendingFile, setPendingFile] = useState(null); // bag photo File, if scanned
  const [card, setCard] = useState(null); // { bean, brew, cups, rationale }

  // Show/hide the whole bag card. Expanded by default; the choice sticks for the
  // life of the morning session so a collapsed card doesn't spring back open as
  // phases advance.
  const [open, setOpen] = useState(true);

  // --- Generate the first recipe for a bean and show the card. ---
  const generateAndShow = async (bean, source) => {
    let brew, rationale;
    try {
      const recipe = await generateBrewRecipe({
        getToken, bean, tasteProfile: store.tasteProfile, mode, cups, source,
        grinder: store.gear?.grinder,
      });
      brew = recipeToBrew(recipe, bean);
      rationale = recipe?.rationale || null;
    } catch (e) {
      // Transport error → deterministic fallback (never a dead end).
      console.warn("brew-recipe generate failed, falling back", e?.message);
      const fb = storeRef.current.computeBrew({ beanId: bean.id, cups, mode });
      brew = fb ? { ...fb } : baselineBrew(bean, cups, mode);
      rationale = fb ? "Calculated from the bag's roast and your taste profile." : brew.rationale;
    }
    // Normalize the provenance the log persists.
    brew.bean_id = bean.id;
    brew.source = source;
    brew.rationale = rationale;
    setCard({ bean, brew, cups, rationale });
    setPhase("recipe");
  };

  // --- Create the bean from the form, then generate. ---
  const createBeanAndGenerate = async (fields) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      let photo_path = null;
      if (pendingFile) {
        try {
          photo_path = await store.uploadEntityPhoto(pendingFile, "coffee");
        } catch (e) {
          console.warn("bag photo upload failed (continuing without)", e?.message);
        }
      }
      const bean = await store.addBean({
        name: fields.name,
        roaster: fields.roaster,
        roast_level: fields.roast_level,
        process: fields.process,
        roast_date: fields.roast_date,
        origin: fields.origin,
        weight_g: fields.label_weight_g,
        weight_left_g: fields.label_weight_g,
        status: "active",
        ...(photo_path ? { photo_path } : {}),
        details: { vision_raw: detected || null },
      });
      await generateAndShow(bean, "vision_initial");
    } catch (e) {
      setError(e?.message || "Couldn't add the bag. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // --- Re-open a bag from history: brew from the existing bean directly. ---
  const reopenFromHistory = async (bean) => {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      await generateAndShow(bean, "history_reopen");
    } catch (e) {
      setError(e?.message || "Couldn't build a recipe. Try again.");
    } finally {
      setBusy(false);
    }
  };

  // --- "Started brew": the one transaction (log → schedule push → complete). ---
  const startBrew = async () => {
    if (busy || !card?.brew) return;
    setBusy(true); setError(null);
    try {
      const log = await store.logBrew({ ...card.brew, bean_id: card.bean.id });
      try {
        await scheduleRatingPush({
          supabase, userId: store.userId, brewLogId: log.id, beanName: card.bean.name,
          fireAt: new Date(Date.now() + 40 * 60 * 1000),
        });
      } catch (e) { console.warn("rating push schedule failed", e?.message); }
      await store.completeMorning(log.id);
      onComplete?.(log);
    } catch (e) {
      setError(e?.message || "Couldn't log the brew. Try again.");
      setBusy(false); // leave the card up so they can retry
    }
  };

  // Vision confirm → editable form (prefilled; low-confidence still lands here).
  const onVisionConfirm = (result, file) => {
    const low = result?.partial === true || result?.confidence === "low";
    setFormInitial({ ...(result || {}), _lowConfidence: low });
    setPendingFile(file || null);
    setPhase("form");
  };

  const subtitle = phase === "recipe" ? "First recipe for this bag." : "Add the bag, then I'll dial the first recipe.";
  // Collapsed, the header still has to say what's behind it.
  const collapsedSummary = card ? `${card.bean.name} · recipe ready` : "Bag setup hidden.";

  return (
    <Card embedded={embedded}>
      <div className={`flex items-start gap-2 ${open ? "mb-4" : ""}`}>
        <div className="min-w-0 flex-1">
          {!embedded && <Eyebrow className="mb-1">New bag</Eyebrow>}
          <div className="text-[12px] text-pronto-text-3">
            {open ? subtitle : collapsedSummary}
          </div>
        </div>
        <button
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls="newbag-body"
          aria-label={open ? "Hide new bag card" : "Show new bag card"}
          className="flex-shrink-0 -mt-0.5 -mr-1 px-1.5 py-1 rounded-[9px] flex items-center gap-1 font-sf text-[12px] font-semibold text-pronto-navy"
        >
          {open ? "Hide" : "Show"}
          <ChevronDown size={13} className={open ? "" : "-rotate-90"} />
        </button>
      </div>

      <div id="newbag-body" hidden={!open}>
        {error && (
          <div className="text-[12.5px] text-pronto-warn bg-[var(--pg-6)] rounded-[11px] px-3 py-2 mb-3">{error}</div>
        )}

        {/* CHOOSE — two paths, plus a manual escape (never a dead end) */}
        {phase === "choose" && (
          <div className="space-y-4">
            <PhotoCapture
              identify={(f) => visionBagPhoto({ getToken, file: f })}
              onConfirm={onVisionConfirm}
              onCancel={() => setPendingFile(null)}
              entity="coffee"
            />
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-[var(--p-sep)]" />
              <span className="font-sf text-[11px] uppercase tracking-[0.08em] text-pronto-text-3">or</span>
              <div className="flex-1 h-px bg-[var(--p-sep)]" />
            </div>
            <div className="flex gap-2">
              <TonalButton onClick={() => setPhase("history")} className="flex-1">Choose from history</TonalButton>
              <SecondaryButton
                onClick={() => { setFormInitial({}); setPendingFile(null); setPhase("form"); }}
                className="flex-1"
              >
                Enter manually
              </SecondaryButton>
            </div>
          </div>
        )}

        {/* HISTORY — re-open an existing bag */}
        {phase === "history" && (
          <HistoryPicker
            beans={store.beans}
            busy={busy}
            onPick={reopenFromHistory}
            onBack={() => setPhase("choose")}
          />
        )}

        {/* FORM — editable bean fields (prefilled from vision or empty) */}
        {phase === "form" && (
          <BeanForm
            initial={formInitial}
            mode={mode}
            setMode={setMode}
            cups={cups}
            setCups={setCups}
            busy={busy}
            onSubmit={createBeanAndGenerate}
            onBack={() => (detected ? null : setPhase("choose"))}
          />
        )}

        {/* RECIPE — gated behind an added bag */}
        {phase === "recipe" && card && (
          <>
            <RecipeCard bean={card.bean} brew={card.brew} cups={card.cups} rationale={card.rationale} />
            {/* Aiden profile banner — a new bag saves a brand-new profile */}
            <RecipeNotes beans={[card.bean]} brew={card.brew} grinder={store.gear?.grinder} className="mb-3" />

            <AidenProfileBanner store={store} bean={card.bean} brew={card.brew} branch="new_bag" className="mb-3" />
            <button
              onClick={startBrew}
              disabled={busy}
              className="w-full h-[46px] rounded-[14px] font-sf text-[15px] font-semibold text-pronto-navy disabled:opacity-60"
              style={{ background: "rgba(63,74,99,0.1)" }}
            >
              {busy ? "Logging…" : "Started the Aiden ✓"}
            </button>
          </>
        )}
      </div>
    </Card>
  );
}
